import { z } from 'zod';

import {
  CONNECTION_CHECK_STATES,
  CONNECTION_CHECK_STATUSES,
  CONNECTION_CHECK_WARNINGS,
} from './constants';

type ConnectionCheckWarningCode = (typeof CONNECTION_CHECK_WARNINGS)[number];

function isKnownWarning(code: string): code is ConnectionCheckWarningCode {
  return (CONNECTION_CHECK_WARNINGS as readonly string[]).includes(code);
}

export const zodConnectionCheckStatusSchema = z.enum(CONNECTION_CHECK_STATUSES);

export const zodConnectionCheckStepSchema = z.object({
  detail: z.string().nullish(),
  durationMs: z.number(),
  /** Field names the read returned, e.g. a file's columns. */
  fields: z.array(z.string()).nullish(),
  httpStatus: z.number().nullish(),
  itemCount: z.number().nullish(),
  // Open-ended, so a step kind added to the API cannot invalidate a check.
  kind: z.string().nullish(),
  name: z.string(),
  status: zodConnectionCheckStatusSchema,
  subject: z.string().nullish(),
  target: z.string().nullish(),
});

/** A value the partner system offers for a setting. */
export const zodConnectionCheckOptionSchema = z.object({
  /** Values other settings must have for this option to apply. */
  dependsOn: z.record(z.string(), z.string()).nullish(),
  label: z.string(),
  /** The option this one sits below, for values that form a tree. */
  parent: z.string().nullish(),
  value: z.string(),
});

export const zodConnectionCheckResultSchema = z.object({
  checkedAt: z.string(),
  durationMs: z.number(),
  /** Values the partner offers, keyed by the name of the setting they fill. */
  options: z
    .record(z.string(), z.array(zodConnectionCheckOptionSchema))
    .default({}),
  status: zodConnectionCheckStatusSchema,
  steps: z.array(zodConnectionCheckStepSchema).default([]),
  /** Values the check read for settings, keyed like the options. */
  suggestions: z.record(z.string(), z.string()).default({}),
  // A warning added to the API is left out until it has a text.
  warnings: z
    .array(z.string())
    .default([])
    .transform((codes) => codes.filter(isKnownWarning)),
});

/**
 * A started or polled check. `state: failed` means the check itself broke and
 * has no result.
 */
export const zodConnectionCheckSchema = z.object({
  createdAt: z.string().optional(),
  id: z.uuid(),
  result: zodConnectionCheckResultSchema.nullish(),
  state: z.enum(CONNECTION_CHECK_STATES),
  target: z.string().optional(),
});
